/**
 * HR Slack integration API client.
 *
 * NOTE — none of these endpoints exist yet in services/api. Every function
 * here has a TODO comment referencing the route that needs to be created.
 * Until those routes land, callers catch the 404 ApiError and fall back to
 * the placeholder "—" state surfaced via `data-todo` attributes on the page.
 */

import { apiRequest } from './client'

// ── Types ────────────────────────────────────────────────────────────────────

export type SlackEventKey =
  | 'leave.submitted'
  | 'leave.approved'
  | 'leave.rejected'
  | 'leave.escalated'
  | 'leave.cancelled'

export interface SlackEventRouting {
  key: SlackEventKey
  channel: string
  dmActor: boolean
  active: boolean
}

export interface SlackWorkspaceConfig {
  connected: boolean
  workspaceDomain: string | null
  connectedByName: string | null
  connectedAt: string | null
  botUsername: string | null
  workspaceId: string | null
  tokenScopes: string[]
  lastTokenRotation: string | null
  eventRoutings: SlackEventRouting[]
}

export interface SlackActivityRow {
  when: string
  eventKey: SlackEventKey
  actorName: string
  target: string
}

export interface SlackActivityLog {
  rows: SlackActivityRow[]
}

export interface SlackRoutingUpdateBody {
  routings: SlackEventRouting[]
}

// ── API functions ─────────────────────────────────────────────────────────────

/**
 * GET /api/v1/hr/slack/config
 * Returns workspace connection state, granted scopes, and event routing table.
 * Requires HR role. Auth via session cookie + RLS.
 */
export async function getSlackConfig(): Promise<SlackWorkspaceConfig> {
  return apiRequest<SlackWorkspaceConfig>('/api/v1/hr/slack/config')
}

/**
 * PATCH /api/v1/hr/slack/config/routings
 * Saves the full event routing table (channel + dmActor + active per event).
 * Body: SlackRoutingUpdateBody.
 */
export async function updateSlackRoutings(
  body: SlackRoutingUpdateBody,
): Promise<SlackWorkspaceConfig> {
  return apiRequest<SlackWorkspaceConfig>('/api/v1/hr/slack/config/routings', {
    method: 'PATCH',
    body,
  })
}

/**
 * POST /api/v1/hr/slack/test
 * Posts a Slack test message. With no `event` (the top-level button) it sends
 * the generic connector test card; with an `event` (a routing-row "Test"
 * button) it previews that specific event's message, routed to the event's
 * configured channel when set. Returns { ok, detail } on success.
 */
export async function sendSlackTestDm(
  event?: SlackEventKey,
): Promise<{ ok: boolean; detail?: string }> {
  return apiRequest<{ ok: boolean; detail?: string }>('/api/v1/hr/slack/test', {
    method: 'POST',
    body: event ? { event } : {},
  })
}

/**
 * GET /api/v1/hr/slack/activity
 * Returns the last 20 webhook dispatch events, newest-first.
 * Each row: { when (relative timestamp), eventKey, actorName, target }.
 */
export async function getSlackActivity(): Promise<SlackActivityLog> {
  return apiRequest<SlackActivityLog>('/api/v1/hr/slack/activity')
}

/**
 * POST /api/v1/hr/slack/disconnect
 * Revokes the workspace connection and bot token. Irreversible until
 * HR re-authorises via Slack OAuth.
 */
export async function disconnectSlack(): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>('/api/v1/hr/slack/disconnect', {
    method: 'POST',
  })
}
